import { useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import useUserStore from '../store/userStore';
import { authAPI, setSessionToken } from '../utils/api';
import { clearFixModeState } from '../utils/fixModeAdvanceSignal';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setUser, setProfile, clearOnboardingData } = useUserStore();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Check query parameters first (for magic link - better email client compatibility)
        let magicToken = searchParams.get('magic_token');
        let sessionId = searchParams.get('session_id');
        
        // Fallback to hash parameters (for OAuth callbacks)
        if (!magicToken && !sessionId) {
          const hash = window.location.hash;
          const hashParams = new URLSearchParams(hash.replace('#', ''));
          sessionId = hashParams.get('session_id');
          magicToken = hashParams.get('magic_token');
        }

        // Handle Magic Link authentication
        if (magicToken) {
          try {
            console.log('[AuthCallback] Verifying magic link token');
            const response = await authAPI.verifyMagicLink(magicToken);
            const userData = response.data;
            console.log('[AuthCallback] Magic link verified, session_token:', userData.session_token ? 'received' : 'missing');
            
            // Store session token for API requests
            if (userData.session_token) {
              setSessionToken(userData.session_token);
              console.log('[AuthCallback] Session token stored in localStorage');
              // Verify it was stored
              const storedToken = localStorage.getItem('fitxess_session_token');
              console.log('[AuthCallback] Verified localStorage:', storedToken ? 'token exists' : 'token missing');
            }

            // Account-switch hygiene: if the authenticated user differs from
            // whoever was previously cached in zustand, drop the previous
            // profile and any in-progress onboarding answers so they can't
            // leak into the new user's onboarding payload (allergens,
            // measurements, macros, etc.). Same-user reauth: no clear, so we
            // don't wipe valid in-progress data unnecessarily.
            const prevUserId = useUserStore.getState().user?.user_id;
            
            // Store user in Zustand
            setUser(userData);
            console.log('[AuthCallback] User stored in Zustand, onboarding_complete:', userData.onboarding_complete);

            if (prevUserId && prevUserId !== userData.user_id) {
              console.log('[AuthCallback] Account switch detected (magic link) — clearing stale profile/onboardingData');
              setProfile(null);
              clearOnboardingData();
              clearFixModeState();
            }

            // Clear the URL params
            window.history.replaceState(null, '', '/auth-callback');

            // Redirect based on onboarding status
            if (userData.onboarding_complete) {
              navigate('/home', { replace: true });
            } else {
              navigate('/onboarding/setup', { replace: true });
            }
            return;
          } catch (error) {
            console.error('Magic link verification failed:', error);
            navigate('/login?error=magic_link_invalid', { replace: true });
            return;
          }
        }

        // Handle OAuth (Google) authentication - session already created by backend
        if (sessionId) {
          try {
            // Store the session token immediately
            setSessionToken(sessionId);
            
            // For our direct Google OAuth, the session_id IS the session_token
            // We just need to validate it and get user data
            const response = await authAPI.validateSession(sessionId);
            const userData = response.data;

            // Account-switch hygiene (see magic-link branch above).
            const prevUserId = useUserStore.getState().user?.user_id;

            // Store user in Zustand
            setUser(userData);

            if (prevUserId && prevUserId !== userData.user_id) {
              console.log('[AuthCallback] Account switch detected (OAuth) — clearing stale profile/onboardingData');
              setProfile(null);
              clearOnboardingData();
              clearFixModeState();
            }

            // Clear the hash from URL
            window.history.replaceState(null, '', '/auth-callback');

            // Redirect based on onboarding status
            if (userData.onboarding_complete) {
              navigate('/home', { replace: true });
            } else {
              navigate('/onboarding/setup', { replace: true });
            }
            return;
          } catch (error) {
            console.error('Session validation failed:', error);
            setSessionToken(null); // Clear invalid token
            navigate('/login?error=session_invalid', { replace: true });
            return;
          }
        }

        // No valid auth params found
        console.error('No session_id or magic_token found in callback');
        navigate('/login', { replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser, setProfile, clearOnboardingData, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="w-12 h-12 border-3 border-[#14D5F6] border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="mt-4 text-gray-500">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
